//REACT
import React, {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

// ASSETS
import {
  default as defib,
  default as defibOff,
} from "assets/img/map/marker.defib.svg";

// STYLES
import {
  InterestPointsMenu,
  MapContainer,
  PreloaderWrapper,
} from "./MapStyled";

// LIBRARIES
import mapboxgl, { Map } from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "react-responsive";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Supercluster from "supercluster";

//MISC
import { ButtonType } from "components/Atoms/ButtonAtom/ButtonAtomModel";
import { TextType } from "components/Atoms/CustomText/CustomTextModel";
import { ModalType } from "components/Atoms/ResponsiveModal/ResponsiveModalModel";
import { COLORS, DEVICE_TYPE, SIZE } from "constants/variables";
import modRewrite from "locales/modRewrite";
import mapService from "services/mapService";
import {
  createCustomMarkerElement,
  getBounds,
  IpCategory,
  IpCategorySet,
  IpPoint,
  mapper,
} from "./MapModel";

// REDUX

// COMPONENTS
import ButtonAtom from "components/Atoms/ButtonAtom/ButtonAtom";
import CustomText from "components/Atoms/CustomText/CustomText";
import Preloader from "components/Atoms/Lotties/Preloader";
import MapDropdownAtom from "components/Atoms/MapDropDownAtom/MapDropdownAtom";
import { MapDropdownWrapper } from "components/Atoms/MapDropDownAtom/MapDropdownAtomStyled";
import ResponsiveModal from "components/Atoms/ResponsiveModal/ResponsiveModal";
import { CheckProps } from "components/Atoms/SelectInputFilter/SelectInputFilterModel";
import EventsGirdle from "components/EventsGirdle/EventsGirdle";
import newsAndMediaService from "services/newsAndMediaService";
import formatDate from "utils/formatDate";
import ShelterDetails from "./ShelterDetails";
import { useViewportPoints } from "./hooks/useViewportPoints";
import type { PointBounds } from "services/map/MapPointQuery";
import { useSelector } from "react-redux";
import { selectLanguage } from "slices/LanguageSlice/languageSlice";

mapboxgl.accessToken =
  "pk.eyJ1Ijoib3Jzb25hcGlrZXkiLCJhIjoiY2w4Y3Y4YXFrMTVmYzNxcGJnNnRpNXRvdCJ9.RaCSdr1r6sz6NpyL8vFwqA";

const MapComponent: React.FC = () => {
  // PROPERTIES
  const location = useLocation();
  const { rgxId } = useParams();
  let paramCategoryId = 10;
  if (rgxId !== undefined) {
    const tmpIds: any = rgxId.split("-");
    paramCategoryId = parseInt(tmpIds.pop());
  } else {
    paramCategoryId = location?.state?.id || 10;
  }

  // LIBRARY CONSTANTS
  const { t } = useTranslation();
  const language = useSelector(selectLanguage);
  const isMobile = useMediaQuery({ maxWidth: DEVICE_TYPE.mobileHeader });
  const navigate = useNavigate();

  // STATE CONSTANTS
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth();
  const years = Array.from({ length: 50 }, (_, i) => 2000 + i);
  const currentYear = new Date().getFullYear();
  const currentYearIndexDefault = years.indexOf(currentYear);
  const [year, setYear] = useState<number>(currentYearIndexDefault);
  const [month, setMonth] = useState(currentMonth);
  const [searchTerm, setSearchTerm] = useState("");
  const [checkedState, setCheckedState] = useState<CheckProps>({
    allEvents: true,
    dsuEvents: false,
    partnerEvents: false,
  });

  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<Map | null>(null);
  const [ipPoints, setIpPoints] = useState<IpCategorySet>({});
  const [ipCategories, setIpCategories] = useState<IpCategory[]>([]);
  const [pinInfo, setPinInfo] = useState<any | null>(null);
  const [selectedIpCategory, setSelectedIpCategory] =
    useState<number>(paramCategoryId);
  //eslint-disable-next-line
  const [shownMarkers, setShownMarkers] = useState<mapboxgl.Marker[]>([]);
  const [bounds, setBounds] = useState<PointBounds | null>(null);
  const [queryBounds, setQueryBounds] = useState<PointBounds | null>(null);
  const viewportData = useViewportPoints(selectedIpCategory, language, queryBounds);
  const activePoints = selectedIpCategory === 10 || selectedIpCategory === 11
    ? ipPoints[selectedIpCategory] : viewportData.points;
  useEffect(() => {
    const timer = window.setTimeout(() => setQueryBounds(bounds), 150);
    return () => window.clearTimeout(timer);
  }, [bounds]);
  useEffect(() => {
    if (selectedIpCategory !== 10 && selectedIpCategory !== 11) setIsLoading(viewportData.loading);
  }, [selectedIpCategory, viewportData.loading]);
  //eslint-disable-next-line
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [styleLoaded, setStyleLoaded] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const formatSchedule = (schedule) => {
    return schedule?.map((el: any) => {
      const dayNamesRo = {
        L: "Luni",
        M: "Marți",
        M2: "Miercuri",
        J: "Joi",
        V: "Vineri",
        S: "Sâmbătă",
        D: "Duminică",
      };
      const daysFormatted = el.days
        .map((d: string) => {
          if (d === "M") return dayNamesRo["M"];
          if (d === "L") return dayNamesRo["L"];
          if (d === "J") return dayNamesRo["J"];
          if (d === "V") return dayNamesRo["V"];
          if (d === "S") return dayNamesRo["S"];
          if (d === "D") return dayNamesRo["D"];
          if (d === "Miercuri") return dayNamesRo["M2"];
          return d;
        })
        .join(", ");

      const formatHour = (str: string) => {
        const [hour, minute] = str.split(":");
        return `${parseInt(hour)+1}:${minute}`;
      };

      const hours = el.is24Hour
        ? "NON-STOP"
        : `${formatHour(el.startTime)} - ${formatHour(el.endTime)}`;

      return {
        days: daysFormatted,
        hours,
        available: !el.isIndisponible,
      };
    });
  };

  const doClusters = useCallback((map: Map, clusters) => {
    if (map === null) {
      return false;
    }
    try {
      if (map.getLayer("cluster-count")) {
        map.removeLayer("cluster-count");
      }
      if (map.getLayer("clusters")) {
        map.removeLayer("clusters");
      }
      if (map.getLayer("cluster-casing")) {
        map.removeLayer("cluster-casing");
      }
      if (map.getSource("clusters")) {
        map.removeSource("clusters");
      }
    } catch (e) {
      console.error(e);
    }
    map.addSource("clusters", {
      type: "geojson",
      data: {
        type: "FeatureCollection",
        features: clusters,
      },
    });

    map.addLayer({
      id: "clusters",
      type: "circle",
      source: "clusters",

      paint: {
        "circle-color": COLORS.Background,

        "circle-radius": {
          property: "point_count",
          type: "interval",
          stops: [
            [1, 20],
            [100, 30],
            [750, 40],
          ],
        },
      },
    });

    map.addLayer({
      id: "cluster-casing",
      type: "circle",
      source: "clusters",
      paint: {
        "circle-color": COLORS.Yellow,
        "circle-radius": {
          property: "point_count",
          type: "interval",
          stops: [
            [1, 18],
            [100, 29],
            [750, 39],
          ],
        },
      },
    });

    map.addLayer({
      id: "cluster-count",
      type: "symbol",
      source: "clusters",
      layout: {
        "text-field": "{point_count_abbreviated}",
        "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
        "text-size": 12,
      },
    });
  }, []);

  const doMarkers = useCallback(
    (map, points) => {
      if (map === null) {
        return false;
      }
      const tmpMarkers: mapboxgl.Marker[] = [];
      const usePoints: any = [];
      // console.log("points inside", points);
      points.forEach((el: IpPoint) => {
        if (
          el.longitudine < 90 &&
          el.longitudine > -90 &&
          el.latitudine < 90 &&
          el.latitudine > -90
        ) {
          usePoints.push({
            type: "Feature",
            geometry: {
              type: "Point",
              coordinates: [Number(el.longitudine), Number(el.latitudine)],
            },
            properties: {
              data: el,
            },
          });

          tmpMarkers.push(
            new mapboxgl.Marker({
              element: createCustomMarkerElement(
                el.icon,
                SIZE.max100,
                (data: any) => {
                  setPinInfo(data);
                },
                el
              ),
            }).setLngLat([el.longitudine, el.latitudine])
          ); // Marker location [lng, lat]
        }
        // console.log("usePoints", usePoints);
      });

      setShownMarkers((prevState) => {
        prevState.forEach((el: mapboxgl.Marker) => {
          if (el) {
            el.remove();
          }
        });

        const radius = tmpMarkers.length > 80 ? 100 : 50;
        const zoom = tmpMarkers.length > 80 ? 14 : 8;

        if (map.getZoom() > zoom + 1) {
          tmpMarkers.forEach((el: mapboxgl.Marker) => el.addTo(map));
        }
        const initializedCluster = new Supercluster({
          radius: radius, // Radius for clustering
          maxZoom: zoom, // Maximum zoom level to cluster
          minZoom: 1,
        });

        if (initializedCluster) {
          const bbox = getBounds(map);

          initializedCluster.load(usePoints);

          const clusters = initializedCluster.getClusters(
            bbox,
            Math.floor(map.getZoom())
          );
          //TODO: DOUBLE CHECK IF WE NEED THIS OR NOT
          // clusters.forEach((el: any) => {
          //   if (el.properties.point_count === undefined) {
          //     const extraMarker = new mapboxgl.Marker({
          //       element: createCustomMarkerElement(
          //         mapper[selectedIpCategory].icon,
          //         SIZE.max100,
          //         (data: any) => {
          //           setPinInfo(data);
          //         },
          //         el.properties.data
          //       ),
          //     }).setLngLat(el.geometry.coordinates);
          //     tmpMarkers.push(extraMarker);
          //     extraMarker.addTo(map);
          //   }
          // });

          doClusters(map, clusters);
        }
        return tmpMarkers;
      });
    },
    //eslint-disable-next-line
    [doClusters]
  );

  // LIFE CYCLE

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    mapService.getIpCategories().then((response: any) => {
      const ipCats = response.data.map((el: any) => ({
        nume: el.attributes.nume,
        id: el.id,
      }));
      ipCats.push({
        nume: "EVENIMENTE",
        id: 11,
      });
      setIpCategories(ipCats);

      [response.data[selectedIpCategory]].forEach((cat: any) => {
        if (selectedIpCategory === 10) {
          if (cat) {
          }
        }
      });
    });
    //eslint-disable-next-line
  }, []);

  useEffect(() => {}, [pinInfo]);

  useEffect(() => {
    if (!mapContainerRef.current) return;

    setIsLoading(true);
    const initializedMap = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: "mapbox://styles/orsonapikey/cl8d64cck00ab14qqzkx2zawi",
      center: [26.1062, 44.4377],
      zoom: 10,
    });
    initializedMap.on("load", () => {
      setStyleLoaded(true);
      setMap(initializedMap);
      setBounds(getBounds(initializedMap));
      setIsLoading(false);
    });
    initializedMap.on("moveend", () => {
      setBounds(getBounds(initializedMap));
    });

    return () => initializedMap.remove();
  }, []);

  const fetchFilteredEvents = async () => {
    let eventType = "all";
    const filterType = "upcoming";
    if (checkedState.dsuEvents) {
      eventType = "DSU";
    } else if (checkedState.partnerEvents) {
      eventType = "partner";
    }
    console.log("sm", searchTerm, filterType, month, year, eventType);

    const response: any = await newsAndMediaService.getEvents(
      0,
      1000,
      searchTerm,
      filterType,
      month,
      year,
      eventType
    );

    console.log("getMapEvents response", response);
    // console.log("cat", cat);

    const tmp: IpCategorySet = { [selectedIpCategory]: [] };
    tmp[11] = response.data.map((el: any) => {
      console.log("el", el.attributes);
      return {
        id: el.id,
        // icon: mapper[11].icon,
        icon:
          el.attributes.partener !== null
            ? mapper[selectedIpCategory].pastIcon
            : mapper[selectedIpCategory].icon,
        ...el.attributes,
      };
    });
    console.log("getMapEvents response", tmp);
    setIpPoints(() => tmp);
  };

  useEffect(() => {
    setPinInfo(null);
    if (selectedIpCategory !== 10 && selectedIpCategory !== 11) return;
    const controller = new AbortController();
    setIsLoading(true);
    const loadSpecialCategory = async () => {
      let points: IpPoint[];
      if (selectedIpCategory === 10) {
        const response: any = await mapService.getDefibrilators(controller.signal);
        points = [...response.On, ...response.On112, ...response.Off, ...response.Off112].map((entry) => ({
          ...entry,
          icon: entry.isOn ? defib : defibOff,
          latitudine: Number(entry.latitudine),
          longitudine: Number(entry.longitudine),
          numar_telefon: entry.numar_telefon !== "" ? entry.numar_telefon : entry.defibrilator.phone,
          defibrilator: { ...entry.defibrilator, defibrilator_schedules_id: formatSchedule(entry.defibrilator.defibrilator_schedules_id) },
        }));
      } else {
        const eventType = checkedState.dsuEvents ? "DSU" : checkedState.partnerEvents ? "partner" : "all";
        const response = await newsAndMediaService.getEvents(1, 1000, searchTerm, "month", month, year, eventType);
        points = response.data.map((entry) => ({
          id: entry.id,
          icon: entry.attributes.partener !== null ? mapper[11].pastIcon : mapper[11].icon,
          ...entry.attributes,
        }));
      }
      if (!controller.signal.aborted) setIpPoints((previous) => ({ ...previous, [selectedIpCategory]: points }));
    };
    loadSpecialCategory().catch((error) => {
      if (!controller.signal.aborted) console.error("Failed to load map category", error);
    }).finally(() => {
      if (!controller.signal.aborted) setIsLoading(false);
    });
    return () => controller.abort();
    // Existing event filters are applied explicitly by fetchFilteredEvents.
    //eslint-disable-next-line
  }, [selectedIpCategory, language]);

  useEffect(() => {}, [map, ipCategories, ipPoints]);

  useEffect(() => {
    // console.log("ipPoints", ipPoints);
    if (
      selectedIpCategory &&
      activePoints !== undefined &&
      map !== null &&
      map !== undefined
    ) {
      const bounds = map.getBounds();
      const bbox = [
        bounds.getWest(),
        bounds.getSouth(),
        bounds.getEast(),
        bounds.getNorth(),
      ];

      const points = activePoints.filter((el: IpPoint) => {
        return (
          el.latitudine <= bbox[3] &&
          el.latitudine >= bbox[1] &&
          el.longitudine <= bbox[2] &&
          el.longitudine >= bbox[0]
        );
      });
      // console.log("points test", points);
      doMarkers(map, points);
      setIsLoading(() => false);
    }
    //eslint-disable-next-line
  }, [map, selectedIpCategory, bounds, activePoints]);

  // EVENT HANDLERS
  const handleResize = () => {
    setScreenWidth(window.innerWidth);
  };

  return styleLoaded !== null ? (
    <>
      <MapContainer>
        {viewportData.error && <div role="alert" style={{ position: "absolute", bottom: 24, left: 24, zIndex: 1600, background: "white", padding: 16 }}>{viewportData.error}</div>}
        <div ref={mapContainerRef} style={{ position: "absolute", inset: 0 }} />
        {!isMobile ? (
          <InterestPointsMenu>
            {ipCategories.map((el: IpCategory, index: number) => {
              return (
                <li key={index}>
                  <ButtonAtom
                    text={t(`components.ipCategories.${mapper[el.id]["name"]}`)}
                    type={
                      el.id === selectedIpCategory
                        ? ButtonType.MapActive
                        : ButtonType.Audio
                    }
                    leftIcon={false}
                    rightIcon={false}
                    onClick={() => {
                      setSelectedIpCategory(el.id);
                      navigate(
                        `/harta/${modRewrite(el.nume.replace(/_/g, "-"))}-${
                          el.id
                        }`
                      );
                    }}
                  />
                </li>
              );
            })}
          </InterestPointsMenu>
        ) : (
          <MapDropdownWrapper>
            <MapDropdownAtom
              options={ipCategories.map((el: any, index) => ({
                label: t(`components.ipCategories.${mapper[el.id]["name"]}`),
                value: el.id,
                key: index,
              }))}
              title={t("map.dropDown.title")}
              selected={selectedIpCategory}
              dropdownName={t(
                `components.ipCategories.${mapper[selectedIpCategory]["name"]}`
              )}
              onConfirm={(values) => {
                setSelectedIpCategory(values[0].value);
              }}
            />
          </MapDropdownWrapper>
        )}
        {pinInfo && (
          <ResponsiveModal
            type={
              selectedIpCategory === 10
                ? ModalType.Defib
                : selectedIpCategory === 11
                ? ModalType.Event
                : ModalType.Info
            }
            id={pinInfo.id}
            onClose={() => {
              setPinInfo(null);
            }}
            isOpen={pinInfo !== null}
            date={
              selectedIpCategory === 11 &&
              formatDate(pinInfo.start_date, pinInfo.end_date, "ro")
            }
            selectedIpCategory={selectedIpCategory}
            title={pinInfo?.titlu || pinInfo?.title}
            canCallEmergency={pinInfo?.canCallEmergency}
            photo={
              pinInfo?.defibrilator?.photo?.url ||
              pinInfo?.photo?.data?.attributes?.url
            }
            phone={pinInfo?.numar_telefon}
            adress={
              pinInfo?.adresa ||
              `${pinInfo.address || ""} , ${pinInfo.city || ""}`
            }
            website={pinInfo?.website}
            long={pinInfo?.longitudine}
            lat={pinInfo?.latitudine}
            schedule={pinInfo?.schedule}
            partener={pinInfo?.partener}
            stats={
              pinInfo.defibrilator &&
              Array.isArray(pinInfo?.defibrilator?.defibrilator_schedules_id)
                ? pinInfo?.defibrilator?.defibrilator_schedules_id.map(
                    (el: any) => el.available
                  )
                : null
            }
          >
            {selectedIpCategory === 5 && pinInfo.civil_shelter?.data && (
              <ShelterDetails shelter={pinInfo.civil_shelter.data.attributes} />
            )}
            {pinInfo.defibrilator &&
              pinInfo.defibrilator?.defibrilator_schedules_id?.length > 0 &&
              pinInfo.defibrilator?.defibrilator_schedules_id.map((el: any) => (
                <Fragment key={el.id}>
                  <CustomText text={el.days + "\n"} type={TextType.H4bold} />
                  <CustomText text={el.hours} type={TextType.P1} />
                </Fragment>
              ))}
          </ResponsiveModal>
        )}
        {selectedIpCategory === 11 && (
          <EventsGirdle
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            setCheckedState={setCheckedState}
            checkedState={checkedState}
            year={year}
            setYear={setYear}
            month={month}
            setMonth={setMonth}
            fetchFilteredEvents={fetchFilteredEvents}
          />
        )}
      </MapContainer>
      {isLoading && (
        <PreloaderWrapper>
          <Preloader width={100} height={100} />
        </PreloaderWrapper>
      )}
    </>
  ) : (
    <PreloaderWrapper>
      <Preloader width={100} height={100} />
    </PreloaderWrapper>
  );
};

export default MapComponent;
